import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { roleContext } from './RoleContext';
import { UserRole } from '../types/role';
import { Shop, ShopBarber } from '../types/shop';
import { Barber } from '../types/barber';
import {
  getShopById,
  getBarberById,
  getShopBarberById,
  getPartnerByEmail,
} from '../middleware/registration_service/getRequests';
import { useAuth0 } from '@auth0/auth0-react';
import { PartnerDetails } from '../types/partner';

interface ProfileDataContextValue {
  role: string | null;
  shopId: bigint;
  barberId: bigint;
  shop: Shop | null;
  barber: Barber | null;
  shopBarber: ShopBarber | null;
  partner: PartnerDetails | null;
  loading: boolean;
  setShop: React.Dispatch<React.SetStateAction<Shop | null>>;
  setBarber: React.Dispatch<React.SetStateAction<Barber | null>>;
  setShopBarber: React.Dispatch<React.SetStateAction<ShopBarber | null>>;
  refreshShop: () => Promise<void>;
  refreshBarber: () => Promise<void>;
  refreshShopBarber: () => Promise<void>;
}

const noop = async () => {};

export const ProfileDataContext = createContext<ProfileDataContextValue>({
  role: null,
  shopId: BigInt(0),
  barberId: BigInt(0),
  shop: null,
  barber: null,
  shopBarber: null,
  partner: null,
  loading: true,
  setShop: () => {},
  setBarber: () => {},
  setShopBarber: () => {},
  refreshShop: noop,
  refreshBarber: noop,
  refreshShopBarber: noop,
});

export const useProfileData = () => useContext(ProfileDataContext);

/**
 * Fetches shop/barber/shopBarber/partner data once per profile route and
 * shares it with all descendant components via context, instead of each
 * component independently calling the registration service.
 */
export default function ProfileDataProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const role = useContext(roleContext);
  const { getAccessTokenSilently } = useAuth0();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const shopIdStr = localStorage.getItem('reg_shop_id');
  const partnerIdStr = localStorage.getItem('reg_partner_id');
  const shopId = BigInt(shopIdStr ? shopIdStr : 0);
  const barberId = BigInt(partnerIdStr ? partnerIdStr : 0);

  const [shop, setShop] = useState<Shop | null>(null);
  const [barber, setBarber] = useState<Barber | null>(null);
  const [shopBarber, setShopBarber] = useState<ShopBarber | null>(null);
  const [partner, setPartner] = useState<PartnerDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshShop = useCallback(async () => {
    if (!shopId) return;
    const res = await getShopById(shopId);
    setShop(res.data);
  }, [shopId]);

  const refreshBarber = useCallback(async () => {
    if (!barberId) return;
    const res = await getBarberById(barberId);
    setBarber(res.data);
  }, [barberId]);

  const refreshShopBarber = useCallback(async () => {
    if (!barberId) return;
    const res = await getShopBarberById(barberId);
    setShopBarber(res.data);
  }, [barberId]);

  useEffect(() => {
    let cancelled = false;
    const link =
      role === UserRole.SHOP_OWNER
        ? 'shopOwners'
        : role === UserRole.BARBER
        ? 'barbers'
        : role === UserRole.SHOP_BARBER
        ? 'shopBarbers'
        : null;

    const load = async () => {
      setLoading(true);
      try {
        const tasks: Promise<void>[] = [];

        if (user.email && link) {
          const token = await getAccessTokenSilently();
          tasks.push(
            getPartnerByEmail(user.email, link, token).then((res) => {
              if (!cancelled && res?.data) setPartner(res.data);
            })
          );
        }

        if (role === UserRole.SHOP_OWNER) {
          tasks.push(refreshShop());
        } else if (role === UserRole.BARBER) {
          tasks.push(refreshBarber());
        } else if (role === UserRole.SHOP_BARBER) {
          // Header (Profile) needs the shop; services/hours need the shopBarber record.
          tasks.push(refreshShop(), refreshShopBarber());
        } else {
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('usertoken');
        }

        await Promise.all(tasks);
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [role]);

  const value = useMemo(
    () => ({
      role,
      shopId,
      barberId,
      shop,
      barber,
      shopBarber,
      partner,
      loading,
      setShop,
      setBarber,
      setShopBarber,
      refreshShop,
      refreshBarber,
      refreshShopBarber,
    }),
    [
      role,
      shopId,
      barberId,
      shop,
      barber,
      shopBarber,
      partner,
      loading,
      refreshShop,
      refreshBarber,
      refreshShopBarber,
    ]
  );

  return (
    <ProfileDataContext.Provider value={value}>
      {children}
    </ProfileDataContext.Provider>
  );
}
